import React, { Component } from 'react';

import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader/root';
import { compose } from 'redux';

import AJAX from 'common/AJAX';
import LoginForm from 'common/auth/LoginForm';
import UserSignupForm from 'common/auth/UserSignupForm';
import ContentContainer from 'common/containers/ContentContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import ForceAdminOAuth from 'common/ForceAdminOAuth';
import Spinner from 'common/Spinner';
import Tappable from 'common/Tappable';
import devURL from 'common/util/devURL';
import getCookies from 'common/util/getCookies';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import withContexts from 'common/util/withContexts';

import 'css/components/www/_LandingAcceptInvite.scss';

class LandingAcceptInvite extends Component {
  static propTypes = {
    location: PropTypes.shape({
      query: PropTypes.shape({
        companyName: PropTypes.string,
        inviteID: PropTypes.string,
        subdomain: PropTypes.string,
      }),
    }),
    viewer: PropTypes.object,
  };

  state = {
    error: null,
    loginMode: false,
    authSettings: null,
    loadingAuthSettings: true,
  };

  onAuthSuccess = () => {
    this.addAndRedirectUser(true);
  };

  onToggleLoginMode = () => {
    this.setState({
      loginMode: !this.state.loginMode,
    });
  };

  fetchAuthSettings = async (subdomain) => {
    try {
      const response = await AJAX.post('/api/company/getAuthSettings', {
        subdomain,
      });

      const { parsedResponse, error } = parseAPIResponse(response, {
        isSuccessful: (response) => response && typeof response === 'object',
        errors: {
          default: 'Something went wrong. Please try again or contact support.',
          'company not found': 'Your invite is invalid, are you sure you copied the right link?',
          'invalid input': 'Your invite is invalid, are you sure you copied the right link?',
        },
      });

      if (error) {
        this.setState({
          error: error.message,
          authSettings: { forcedMemberAuthMechanism: null },
          loadingAuthSettings: false,
        });
        return;
      }

      const { companyID, forcedMemberAuthMechanism } = parsedResponse;

      this.setState({
        authSettings: { companyID, forcedMemberAuthMechanism },
        loadingAuthSettings: false,
      });
    } catch (error) {
      this.setState({
        error: 'Something went wrong. Please try again or contact support.',
        authSettings: { forcedMemberAuthMechanism: null },
        loadingAuthSettings: false,
      });
    }
  };

  componentDidMount() {
    const {
      location: { query },
      viewer,
    } = this.props;

    // Case #1: viewer is already a member, redirect
    const viewerIsMember =
      viewer &&
      viewer.companies &&
      viewer.companies.some((company) => {
        company.subdomain === query.subdomain;
      });
    if (viewerIsMember) {
      window.location = devURL(`https://${query.subdomain}.canny.io/admin`);
      return;
    }

    if (!query.inviteID || !query.companyName || !query.subdomain) {
      window.location = devURL(`https://canny.io`);
      return;
    }

    // Case #2: logged in but with different email, show error
    if (query.email && viewer && !viewer.loggedOut && viewer.email !== query.email) {
      this.setState({
        error: `You are accepting an invite for a different email. Please log in with the correct email (${query.email}).`,
      });
      return;
    }

    // Case #3: not member but logged in, add them
    if (viewer._id) {
      this.addAndRedirectUser(false);
      return;
    }

    // Case #4: not logged in, fetch auth settings and show sign up / log in form
    this.fetchAuthSettings(query.subdomain);
  }

  async addAndRedirectUser(needsRefresh) {
    const {
      location: { query },
    } = this.props;
    const [hubspotutk] = getCookies(['hubspotutk']);
    const response = await AJAX.post('/api/company/joinTeam', {
      hubspotutk,
      inviteID: query.inviteID,
      subdomain: query.subdomain,
    });

    const { error } = parseAPIResponse(response, {
      isSuccessful: isDefaultSuccessResponse,
      errors: {
        default: "Your inviteID didn't work, are you sure you copied the right link?",
        'plan does not support': 'Your team cannot accept members for this role',
        'could not find role': 'The role you are trying to accept does not exist',
        'invite expired': 'Your inviteID expired, please check your latest emails!',
        'email mismatch': `You are accepting an invite for a different email. Log in with the correct email (${query.email}).`,
        'admin limit':
          'Your team has reached the admin limit. Please contact support to increase your limit or invite users as contributors only.',
      },
    });

    if (error) {
      this.setState({ error: error.message });
      return;
    }

    window.location = devURL(`https://${query.subdomain}.canny.io/admin`);
    return;
  }

  renderAuthForm() {
    const {
      location: { query },
      viewer,
    } = this.props;
    if (viewer && viewer._id) {
      return null;
    }

    const title = `Join ${query.companyName} on Canny`;
    const { authSettings, loadingAuthSettings } = this.state;

    // Show loading state while fetching auth settings
    if (loadingAuthSettings) {
      return (
        <div className="authForm card">
          <div className="topContainer">
            <h1>{title}</h1>
            <Spinner size="small" />
          </div>
        </div>
      );
    }

    // If forced auth is required, show only the ForceAdminOAuth component
    // except for okta, since we don't currently support it in ForceAdminOAuth
    if (
      authSettings?.forcedMemberAuthMechanism &&
      authSettings.forcedMemberAuthMechanism !== 'okta'
    ) {
      return (
        <div className="authForm card">
          <div className="topContainer">
            <h1 className="!m-0">{title}</h1>
            <ForceAdminOAuth
              companyID={authSettings.companyID}
              forcedMemberAuthMechanism={authSettings.forcedMemberAuthMechanism}
              onFailure={(error) => this.setState({ error: error.message })}
              onSuccess={this.onAuthSuccess}
            />
          </div>
        </div>
      );
    }

    // Default auth form with regular login/signup
    if (this.state.loginMode) {
      return (
        <div className="authForm card">
          <div className="topContainer">
            <h1>{title}</h1>
            <LoginForm
              acceptedEmail={query.email}
              onSuccess={this.onAuthSuccess}
              queryAllowlist={{ inviteID: true }}
            />
          </div>
          <Tappable onTap={this.onToggleLoginMode}>
            <div className="toggleButton">
              Need an account? <div className="emphasize">Sign up</div>
            </div>
          </Tappable>
        </div>
      );
    } else {
      return (
        <div className="authForm card">
          <div className="topContainer">
            <h1>{title}</h1>
            <UserSignupForm
              acceptedEmail={query.email}
              onSuccess={this.onAuthSuccess}
              queryAllowlist={{ inviteID: true }}
              signupCTA="Join"
            />
          </div>
          <Tappable onTap={this.onToggleLoginMode}>
            <div className="toggleButton">
              Already have an account? <div className="emphasize">Log In</div>
            </div>
          </Tappable>
        </div>
      );
    }
  }

  renderErrorMessage() {
    if (!this.state.error) {
      return null;
    }

    return <div className="card error">{this.state.error}</div>;
  }

  render() {
    return (
      <ContentContainer innerClassName="landingAcceptInvite">
        {this.renderErrorMessage()}
        {this.renderAuthForm()}
      </ContentContainer>
    );
  }
}

export default compose(
  withContexts({
    viewer: ViewerContext,
  }),
  hot,
)(LandingAcceptInvite);
